import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { api, backendFetch } from './client';

/** Аккаунт партнёра, как его видит сам партнёр: без токенов и наших меток. */
export interface PartnerAccount {
  id: string;
  platform: 'instagram' | 'tiktok' | 'youtube' | 'threads';
  username: string;
  profilePicture: string | null;
  followersCount: number | null;
  mediaCount: number | null;
  connectedAt: string | null;
  tokenExpiry: string | null;
}

/** Публикация партнёрского аккаунта: последний ночной снимок. */
export interface PartnerPost {
  mediaId: string;
  accountId: string;
  account: string | null;
  link: string | null;
  thumbnail: string | null;
  publishedAt: string;
  mediaType: string | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  asOf: string;
}

export interface PartnerReportRow {
  id: string;
  name: string;
  login: string;
  accounts: { id: string; username: string; followersCount: number | null }[];
  posts: number;
  views: number | null;
  items: PartnerPost[];
}

export interface PartnerInvite {
  token: string;
  name: string;
  createdAt: string;
  usedAt: string | null;
  partner: { id: string; login: string; accounts: string[] } | null;
}

const SERVER_URL = `https://${projectId}.supabase.co/functions/v1/make-server-bde4f709`;

/**
 * Приглашение открывают ещё без входа — общий `api` без сессии выходит из
 * неё, поэтому здесь свой запрос с публичным ключом.
 */
async function publicRequest<T>(endpoint: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${SERVER_URL}${endpoint}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${publicAnonKey}`, ...init.headers },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Ошибка ${response.status}`);
  return data as T;
}

export const partnerApi = {
  // Без входа — страница приглашения.
  invite: (token: string) =>
    publicRequest<{ name: string; used: boolean }>(`/partner/invite/${encodeURIComponent(token)}`),
  join: (token: string, login: string, password: string) =>
    publicRequest<{ ok: true; login: string }>('/partner/join', { method: 'POST', body: JSON.stringify({ token, login, password }) }),

  // Партнёр.
  me: () => api.get<{ name: string | null; login: string | null; accounts: PartnerAccount[] }>('/partner/me'),
  connectInstagram: (code: string, redirectUri: string) =>
    api.post<{ account: PartnerAccount; reconnected: boolean }>('/partner/accounts/instagram', { code, redirectUri }),
  /** Вход TikTok — на бэкенде (там ключи приложения), id партнёра — в подписанном state. */
  tiktokAuthUrl: async (): Promise<string> => {
    const res = await backendFetch('/api/tiktok/partner/auth-url');
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.url) throw new Error(data.error || `Ошибка ${res.status}`);
    return data.url;
  },
  /** Вход Threads — так же через бэкенд (28.10.2026). */
  threadsAuthUrl: async (): Promise<string> => {
    const res = await backendFetch('/api/threads/partner/auth-url');
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.url) throw new Error(data.error || `Ошибка ${res.status}`);
    return data.url;
  },

  myPosts: (from: string, to: string) =>
    api.get<{ totals: { posts: number; views: number | null }; posts: PartnerPost[] }>('/partner/me/posts', { params: { from, to } }),

  // Мы: отчёт по партнёрам — админ и руководители отделов.
  report: (from: string, to: string) =>
    api.get<{ totals: { partners: number; posts: number; views: number | null }; partners: PartnerReportRow[] }>('/partner/report', { params: { from, to } }),

  // Админ.
  invites: () => api.get<{ invites: PartnerInvite[] }>('/partner/invites'),
  createInvite: (name: string) => api.post<{ invite: PartnerInvite }>('/partner/invites', { name }),
  revokeInvite: (token: string) => api.delete<{ ok: true }>(`/partner/invites/${encodeURIComponent(token)}`),
};
